/**
 * Agents — co-located typed client for the already-built agent routes
 * (services/quill-admin-backend/src/routes/agent.js, mounted at
 * `/api/admin/quill/agent`).
 *
 * The shared `request`/`unwrap` helpers in services/quillAdminService.ts are
 * module-private (only `QuillApiError` is exported), so — per the P5 Wave 4
 * contract — this file REPLICATES the same tiny fetch/unwrap seam rather than
 * editing the service. Same-origin fetch through the dev-server proxy (which
 * injects the internal key + admin email); no Authorization header, no axios.
 * Throws the shared `QuillApiError` on !ok or `{success:false}` so error copy
 * and `code` handling stay identical to the rest of the Quill admin surface.
 */

import { QuillApiError } from '../../../services/quillAdminService';

const BASE = '/api/admin/quill';

/** Builds a `?a=1&b=2` query string, skipping undefined/null/empty values. */
function qs(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

interface EnvelopeShape {
  success?: boolean;
  code?: string;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
    });
  } catch (e) {
    throw new QuillApiError(
      e instanceof Error ? e.message : 'Network error contacting Quill backend',
      0,
      'NETWORK_ERROR'
    );
  }

  let body: EnvelopeShape | null = null;
  try {
    body = await res.json();
  } catch {
    // No/invalid JSON body — handled below.
  }

  if (!res.ok || (body && body.success === false)) {
    const message = body?.message || body?.error || `Quill backend request failed (${res.status})`;
    throw new QuillApiError(message, res.status, body?.code);
  }
  return (body ?? {}) as T;
}

/** Unwrap the `{ success, data }` envelope → data (falls back to the raw body). */
function unwrap<T>(body: unknown): T {
  const b = body as { data?: T } | null | undefined;
  if (b && typeof b === 'object' && 'data' in b && b.data !== undefined) {
    return b.data as T;
  }
  return body as T;
}

// ════════════════════════════════════════════════════════════════════════
// Types (shaped against agent.js — counts filtered without ::int come back as
// bigint STRINGS from node-pg, so every numeric field is treated as `number |
// string` and coerced with `num()` in the page).
// ════════════════════════════════════════════════════════════════════════

export type CountLike = number | string | null | undefined;

/** One row of GET /agent/definitions — `quill_agent_config` plus live counts. */
export interface AgentDefinition {
  agent_name: string;
  display_name: string | null;
  description: string | null;
  is_enabled: boolean;
  model_override: string | null;
  admin_note: string | null;
  display_order: CountLike;
  updated_by_admin: string | null;
  active_sessions: CountLike;
  completed_7d: CountLike;
  abandoned_7d: CountLike;
  total_7d: CountLike;
}

/** One row of GET /agent/health — 7-day per-agent metrics. */
export interface AgentHealthRow {
  agent_name: string;
  display_name: string | null;
  is_enabled: boolean;
  active_now: CountLike;
  suspended_now: CountLike;
  total_7d: CountLike;
  completed_7d: CountLike;
  abandoned_7d: CountLike;
  avg_events_7d: CountLike;
  stuck_count: CountLike;
  top_abandonment_reason: string | null;
}

/** One row of GET /agent/sessions. */
export interface AgentSession {
  session_id: string;
  agent_name: string;
  quill_member_id: string;
  current_state: string | null;
  status: string;
  events_count: CountLike;
  last_activity_at: string | null;
  created_at: string | null;
  abandoned_reason: string | null;
  agent_display_name: string | null;
}

/** Body for PATCH /agent/definitions/:agent_name. `model_override: null` clears. */
export interface UpdateAgentRequest {
  is_enabled?: boolean;
  model_override?: string | null;
}

export interface SessionQueryParams {
  agent_name?: string;
  status?: string;
  limit?: number;
}

// ════════════════════════════════════════════════════════════════════════
// Client
// ════════════════════════════════════════════════════════════════════════

export const agentsApi = {
  /** GET /agent/definitions — agents + live session counts. */
  async getDefinitions(): Promise<AgentDefinition[]> {
    const body = await request<{ success: true; data: AgentDefinition[] }>('/agent/definitions');
    return unwrap<AgentDefinition[]>(body);
  },

  /**
   * PATCH /agent/definitions/:agent_name — enable/disable or set/clear the
   * model override. This changes live member conversation behaviour; the
   * engine cache reload is fire-and-forget (≤60s). 404 NOT_FOUND if the agent
   * name is unknown, 400 if no updatable fields are sent.
   */
  async updateAgent(agentName: string, payload: UpdateAgentRequest): Promise<AgentDefinition> {
    const body = await request<{ success: true; data: AgentDefinition }>(
      `/agent/definitions/${encodeURIComponent(agentName)}`,
      { method: 'PATCH', body: JSON.stringify(payload) }
    );
    return unwrap<AgentDefinition>(body);
  },

  /** GET /agent/health — 7-day per-agent metrics incl. stuck (>30m) counts. */
  async getHealth(): Promise<AgentHealthRow[]> {
    const body = await request<{ success: true; data: AgentHealthRow[] }>('/agent/health');
    return unwrap<AgentHealthRow[]>(body);
  },

  /** GET /agent/sessions — active/suspended sessions (default status filter). */
  async getSessions(params: SessionQueryParams = {}): Promise<AgentSession[]> {
    const body = await request<{ success: true; data: AgentSession[] }>(`/agent/sessions${qs(params)}`);
    return unwrap<AgentSession[]>(body);
  },

  /**
   * POST /agent/sessions/:session_id/abandon — admin force-abandon a stuck
   * session. 404 NOT_FOUND_OR_ALREADY_TERMINAL if it is already completed or
   * abandoned. Ends a live member conversation immediately.
   */
  async abandonSession(
    sessionId: string
  ): Promise<{ session_id: string; agent_name: string; status: string }> {
    const body = await request<{
      success: true;
      data: { session_id: string; agent_name: string; status: string };
    }>(`/agent/sessions/${encodeURIComponent(sessionId)}/abandon`, { method: 'POST' });
    return unwrap<{ session_id: string; agent_name: string; status: string }>(body);
  },
};

export default agentsApi;
